import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	type GovChipProps,
	GovFlex,
	GovLayout,
	GovLayoutColumn,
} from '@gov-design-system-ce/react';
import { LocalizeNameSpaceTypes, useCmsService } from '@gov-nx/core/service';
import type { ServiceCode } from '@gov-nx/module/service';
import { BackButton, Chip, CookbookList, ToggleButton } from '@gov-nx/ui/web';
import { useTitle } from '@gov-nx/utils/web';
import { FavoriteToggle } from '../Favorite/FavoriteToggle';
import { MarkdownRender } from '../Markdown/MarkdownRender';
import { CmsFaq } from './CmsFaq';
import { CsmNavigationSignpost } from './CsmNavigationSignpost';

export interface CmsServiceRenderProps {
	code: ServiceCode;
	children: ReactNode;
	showFavorite?: boolean;
	showCookbook?: boolean;
	override?: {
		name?: string;
		description?: string;
	};
	chip?: {
		label: string;
		color: GovChipProps['color'];
		type: GovChipProps['type'];
	};
}

export const CmsServiceRender = ({
	code,
	children,
	showFavorite = true,
	showCookbook = true,
	override,
	chip,
}: CmsServiceRenderProps) => {
	const navigate = useNavigate();
	const cmsContent = useCmsService({ code });
	const localizeNameSpace = LocalizeNameSpaceTypes.Service;
	const { t } = useTranslation([localizeNameSpace]);
	const title = override?.name ?? cmsContent?.name;
	const description = override?.description ?? cmsContent?.description;

	useTitle(title);

	if (cmsContent === null) {
		return null;
	}

	return (
		<>
			<main className="flex flex-col gap-(--templates-margin-3xl)!">
				<header className="flex flex-col gap-(--templates-margin-l)">
					<div className="print:hidden">
						<BackButton onClick={() => navigate(-1)} />
					</div>
					<GovFlex
						direction="column"
						gap="m">
						<h1>
							{title}
							{cmsContent.favoriteAllowed && showFavorite ? (
								<FavoriteToggle
									{...{ code, name: title }}
									customClass="relative top-[-7px]"
								/>
							) : null}
						</h1>
						{chip ? (
							<Chip
								color={chip.color}
								type={chip.type}
								size="m"
								className="w-fit">
								{chip.label}
							</Chip>
						) : null}
						{cmsContent.cookbook?.length && showCookbook ? (
							<CookbookList
								label={t('kucharka', { namespace: localizeNameSpace })}
								items={cmsContent.cookbook}
							/>
						) : null}
					</GovFlex>
					{description ? (
						<GovLayout
							type="text"
							variant="left">
							<GovLayoutColumn>
								{cmsContent.descriptionDisplayed ? (
									<MarkdownRender>{description}</MarkdownRender>
								) : (
									<ToggleButton className="hover-reverse">
										<MarkdownRender>{description}</MarkdownRender>
									</ToggleButton>
								)}
							</GovLayoutColumn>
						</GovLayout>
					) : null}
				</header>
				<GovLayout
					type="text"
					variant="left"
					className="pb-(--templates-margin-3xl)">
					<GovLayoutColumn>{children}</GovLayoutColumn>
				</GovLayout>
			</main>
			{cmsContent.navigations.length ? (
				<CsmNavigationSignpost
					navigations={cmsContent.navigations}
					localizeNameSpace={localizeNameSpace}
				/>
			) : null}
			{cmsContent.faq.length ? (
				<CmsFaq
					faq={cmsContent.faq}
					localizeNameSpace={localizeNameSpace}
				/>
			) : null}
		</>
	);
};
